import { useCallback, useEffect, useState } from 'react';

import {
  CRYPTO_NETWORKS,
  CRYPTO_NETWORK_LABELS,
  PAYOUT_METHOD_KINDS,
  PAYOUT_METHOD_LABELS,
  networkWarningSentence,
  validateWalletAddress,
  type CreatorProfileResponse,
  type CryptoNetwork,
  type PayoutMethodKind,
} from '@nexus/shared';

import { ApiError, apiGet, apiSend } from '../lib/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  CheckboxField,
  Fact,
  Facts,
  FieldRow,
  LoadErrorPage,
  LoadingPage,
  Modal,
  PageHeader,
  SelectField,
  StatusPill,
  TextField,
  When,
} from '../ui';

/**
 * Profile and terms.
 *
 * Three things a creator has to be able to check at any time: who Nexus thinks
 * they are, where the money is sent, and which words they agreed to and when.
 *
 * The payout destination is NOT prefilled and cannot be: the API returns a
 * mask, never the value. Replacing it means typing it again, which is the
 * honest consequence of never handing it back to a browser — and the form says
 * so rather than leaving somebody wondering why the box is empty.
 */
export function Profile(): React.JSX.Element {
  const [data, setData] = useState<CreatorProfileResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [country, setCountry] = useState('');
  const [methodKind, setMethodKind] = useState<PayoutMethodKind>('PAYPAL');
  const [methodText, setMethodText] = useState('');
  const [cryptoNetwork, setCryptoNetwork] = useState<CryptoNetwork>('TRON_TRC20');
  /* The confirm step: the network and the address, shown again before the
     save. Sending USDT on the wrong network loses it, and a select box is a
     thing people leave on whatever it was last time. */
  const [confirming, setConfirming] = useState(false);
  const [autoRequest, setAutoRequest] = useState(false);
  const [payoutSaved, setPayoutSaved] = useState(false);
  const [payoutError, setPayoutError] = useState<string | null>(null);

  const isUsdt = methodKind === 'USDT';
  /* Checked as they type, with the same function the Worker runs. */
  const verdict = isUsdt && methodText.trim().length > 0 ? validateWalletAddress(cryptoNetwork, methodText) : null;

  const load = useCallback(async () => {
    setError(null);
    try {
      const body = await apiGet<CreatorProfileResponse>('/creators/me');
      setData(body);
      setDisplayName(body.creator.displayName ?? '');
      setCountry(body.creator.country ?? '');
      setMethodKind(body.payoutProfile.methodKind ?? 'PAYPAL');
      if (body.payoutProfile.cryptoNetwork) setCryptoNetwork(body.payoutProfile.cryptoNetwork);
      setAutoRequest(body.payoutProfile.autoRequest);
      setMethodText('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load your profile.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await apiSend('PATCH', '/creators/me', { displayName, country });
      setSaved(true);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save.');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Save where the money goes.
   *
   * The value is sent once and never comes back: the response carries the
   * mask, so a mistyped address shows as a different mask rather than being
   * re-readable from this page.
   */
  async function savePayout(): Promise<void> {
    setConfirming(false);
    setBusy(true);
    setPayoutError(null);
    setPayoutSaved(false);
    try {
      await apiSend('POST', '/wallet/payout-profile', {
        provider: 'MANUAL',
        methodKind,
        methodText,
        autoRequest,
        ...(isUsdt ? { cryptoNetwork } : {}),
      });
      setPayoutSaved(true);
      setMethodText('');
      await load();
    } catch (err) {
      setPayoutError(err instanceof ApiError ? err.message : 'Could not save your payout details.');
    } finally {
      setBusy(false);
    }
  }

  /** USDT goes through the confirm step; anything else saves straight away. */
  function submitPayout(event: React.FormEvent): void {
    event.preventDefault();
    setPayoutError(null);
    if (isUsdt) {
      if (!verdict || !verdict.ok) {
        setPayoutError(verdict && !verdict.ok ? verdict.message : `Paste your ${CRYPTO_NETWORK_LABELS[cryptoNetwork]} wallet address.`);
        return;
      }
      setConfirming(true);
      return;
    }
    void savePayout();
  }

  async function acceptTerms(): Promise<void> {
    if (!data) return;
    setBusy(true);
    setError(null);
    try {
      await apiSend('POST', '/creators/me/terms', {
        version: data.terms.current.version,
        documentHash: data.terms.current.documentHash,
      });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not record that.');
    } finally {
      setBusy(false);
    }
  }

  if (error && data === null) {
    return (
      <LoadErrorPage
        eyebrow="Creator"
        title="Profile"
        heading="Could not load your profile."
        onRetry={() => void load()}
      >
        <span>{error}</span>
      </LoadErrorPage>
    );
  }

  if (data === null) return <LoadingPage eyebrow="Creator" title="Profile" />;

  return (
    <>
      <PageHeader
        eyebrow="Creator"
        title="Profile"
        lead="Who you are, where your money goes, and which version of the terms you agreed to."
        actions={<StatusPill status={data.creator.status} domain="creator" />}
      />

      {/**
       * WHY THE STATUS PILL WAS NOT ENOUGH.
       *
       * The header carried "Rejected" or "Suspended" as a coloured word and
       * nothing else, so the one screen a creator opens to find out what is
       * wrong with their account told them the name of the state and not one
       * syllable about the cause — while the operator's sentence, written at
       * the moment of the decision precisely so it could be read, sat unused in
       * the database. It is shown verbatim, which is also what the campaign
       * cards do with the same string.
       */}
      {data.creator.status !== 'APPROVED' && (
        <Alert
          tone={data.creator.status === 'PENDING_REVIEW' ? 'waiting' : 'critical'}
          title={
            data.creator.status === 'SUSPENDED'
              ? 'Your creator account is suspended'
              : data.creator.status === 'REJECTED'
                ? 'Your creator account was not approved'
                : 'Your creator account is still being reviewed'
          }
        >
          <span>
            {data.creator.statusReason ??
              (data.creator.status === 'PENDING_REVIEW' || data.creator.status === 'INVITED'
                ? 'You can connect your accounts and read the campaigns now; you can submit clips as soon as an operator has approved you.'
                : 'No reason was recorded.')}
          </span>
        </Alert>
      )}

      {data.terms.reacceptanceRequired && (
        <Alert tone="waiting" title="The creator terms have changed">
          <span>
            Read version {data.terms.current.version} and accept it to keep submitting clips.
            Nothing you have already earned is affected.
          </span>
          {/* "below" was the whole direction, on a page with two other cards
              and two other primary buttons between here and it. */}
          <a href="#terms">Go to the terms</a>
        </Alert>
      )}

      {/* EVEN, not 1.55/1: these are two forms of the same weight, and the
          1.55fr version left "Where your money goes" in a column too narrow for
          the sentence about encryption while "About you" had a third of its own
          row empty. */}
      <div className="split even">
        <Card title="About you">
          <form onSubmit={save} className="form">
            <TextField
              label="Display name"
              type="text"
              name="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              hint="What the leaderboard calls you."
            />
            <TextField
              label="Country"
              type="text"
              name="country"
              value={country}
              onChange={(e) => setCountry(e.target.value.toUpperCase())}
              maxLength={2}
              required
              hint="Two letters, ISO style: US, GB, DE. The payout rail and the tax threshold both depend on it, which is why it is a code and not a name."
            />
            <Button type="submit" variant="primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </form>
          {saved && <Alert tone="positive">Saved.</Alert>}
          {error && <Alert tone="critical">{error}</Alert>}
        </Card>

        <Card title="Where your money goes" sub={data.payoutProfile.placeholderNote}>
          <Facts>
            <Fact label="Rail">
              {data.payoutProfile.provider === 'MANUAL' ? 'Paid by hand' : data.payoutProfile.provider}
            </Fact>
            <Fact label="Method">
              {data.payoutProfile.methodKind ? PAYOUT_METHOD_LABELS[data.payoutProfile.methodKind] : <span className="muted">Not set</span>}
            </Fact>
            {data.payoutProfile.cryptoNetwork && (
              <Fact label="Network">{CRYPTO_NETWORK_LABELS[data.payoutProfile.cryptoNetwork]}</Fact>
            )}
            <Fact label="Sent to">
              {data.payoutProfile.methodLabel ?? <span className="muted">Not set</span>}
            </Fact>
            <Fact label="Status">
              <StatusPill status={data.payoutProfile.status} />
            </Fact>
            <Fact label="Tax details">
              {data.payoutProfile.hasTaxReference ? 'On file' : 'Not yet'}
            </Fact>
          </Facts>

          <form onSubmit={submitPayout} className="form">
            <FieldRow>
              <SelectField
                label="How should we pay you?"
                name="methodKind"
                value={methodKind}
                onChange={(e) => {
                  setMethodKind(e.target.value as PayoutMethodKind);
                  setPayoutError(null);
                }}
              >
                {PAYOUT_METHOD_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {PAYOUT_METHOD_LABELS[kind]}
                  </option>
                ))}
              </SelectField>
              {isUsdt && (
                <SelectField
                  label="Network"
                  name="cryptoNetwork"
                  value={cryptoNetwork}
                  onChange={(e) => setCryptoNetwork(e.target.value as CryptoNetwork)}
                  hint="Asset: USDT (Tether). The chain your wallet receives it on."
                >
                  {CRYPTO_NETWORKS.map((n) => (
                    <option key={n} value={n}>
                      {CRYPTO_NETWORK_LABELS[n]}
                    </option>
                  ))}
                </SelectField>
              )}
            </FieldRow>

            {isUsdt && (
              <Alert tone="waiting" title="Check the network">
                <span data-testid="network-warning">{networkWarningSentence(cryptoNetwork)}</span>
              </Alert>
            )}

            <TextField
              label={isUsdt ? 'Wallet address' : 'Where, exactly?'}
              type="text"
              name="methodText"
              value={methodText}
              onChange={(e) => setMethodText(e.target.value)}
              placeholder={
                data.payoutProfile.hasMethod
                  ? `Replaces ${data.payoutProfile.methodLabel}`
                  : isUsdt
                    ? cryptoNetwork === 'TRON_TRC20'
                      ? 'T…'
                      : cryptoNetwork === 'SOLANA'
                        ? 'Base58, 32–44 characters'
                        : '0x…'
                    : 'grace@example.com'
              }
              minLength={3}
              maxLength={500}
              required
              autoComplete="off"
              spellCheck={false}
              error={verdict && !verdict.ok ? verdict.message : null}
              hint={
                isUsdt ? (
                  <>
                    Paste it from your wallet; it is checked as you type.{' '}
                    {verdict?.ok ? (
                      <span className="addr-preview" data-testid="address-preview">
                        Ends in <span className="addr-tail">…{verdict.last6}</span> — that is what you will see everywhere afterwards.
                      </span>
                    ) : null}{' '}
                    It is encrypted before it is stored and never shown back in full — only the operator paying you sees it, while sending.
                  </>
                ) : (
                  'Your PayPal address, or the account details for a transfer. It is encrypted before it is stored and is never shown back to you — only the operator paying you ever sees it again, and then only while your payout is being sent.'
                )
              }
              {...(payoutError ? { 'aria-invalid': true } : {})}
            />
            <CheckboxField
              name="autoRequest"
              checked={autoRequest}
              onChange={(e) => setAutoRequest(e.target.checked)}
              label="Put me in the Monday batch automatically whenever I am above the minimum"
            />
            <Button type="submit" variant="primary" disabled={busy || (isUsdt && !verdict?.ok)}>
              {busy
                ? 'Saving…'
                : isUsdt
                  ? 'Check and save'
                  : data.payoutProfile.hasMethod
                    ? 'Replace my details'
                    : 'Save my details'}
            </Button>
          </form>
          {payoutSaved && (
            <Alert tone="positive">
              Saved. Payouts go to {data.payoutProfile.methodLabel}
              {data.payoutProfile.cryptoNetwork ? ` on ${CRYPTO_NETWORK_LABELS[data.payoutProfile.cryptoNetwork]}` : ''}.
            </Alert>
          )}
          {payoutError && <Alert tone="critical">{payoutError}</Alert>}

          {confirming && verdict?.ok && (
            <Modal
              title="Is this the right network?"
              onClose={() => setConfirming(false)}
              footer={
                <Button variant="primary" onClick={() => void savePayout()} disabled={busy}>
                  Yes, save it
                </Button>
              }
            >
              <p>
                USDT sent on the wrong network is gone. Check both against your wallet before you
                save.
              </p>
              <dl className="confirm-facts">
                <dt>Asset</dt>
                <dd>USDT</dd>
                <dt>Network</dt>
                <dd data-testid="confirm-network">{CRYPTO_NETWORK_LABELS[cryptoNetwork]}</dd>
                <dt>Address</dt>
                <dd>
                  <code className="chip break" data-testid="confirm-address">
                    {verdict.normalized}
                  </code>
                </dd>
              </dl>
            </Modal>
          )}
        </Card>
      </div>

      <Card
        id="terms"
        title="Terms"
        aside={
          data.terms.accepted ? (
            <Badge tone={data.terms.reacceptanceRequired ? 'waiting' : 'positive'}>
              {data.terms.accepted.version}
            </Badge>
          ) : (
            <Badge tone="critical">Not accepted</Badge>
          )
        }
        sub={
          data.terms.accepted ? undefined : 'You have not accepted the creator terms yet.'
        }
      >
        {data.terms.accepted && (
          <p>
            You accepted <strong>{data.terms.accepted.version}</strong> on{' '}
            <When value={data.terms.accepted.acceptedAt} withTime />.
          </p>
        )}

        {data.terms.reacceptanceRequired && (
          <div className="actions">
            <Button variant="primary" onClick={() => void acceptTerms()} disabled={busy}>
              {busy ? 'Recording…' : `Accept ${data.terms.current.version}`}
            </Button>
          </div>
        )}

        <details className="reveal">
          <summary>Read {data.terms.current.version} in full</summary>
          <pre className="prose-block">{data.terms.current.bodyText}</pre>
        </details>

        <p className="muted">
          Document hash{' '}
          {/* The one shortened value in the app. The whole hash is on the
              element, so "copy the hash" is still a thing a person can do. */}
          <code className="chip quiet break" title={data.terms.current.documentHash}>
            {data.terms.current.documentHash.slice(0, 16)}…
          </code>{' '}
          — what you accepted is recorded against the exact words, not just the version name.
        </p>
      </Card>
    </>
  );
}
